import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './StudentNavbar.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
// 移除首课日期弹窗，改为直接输入
import BrandMark from '../common/BrandMark/BrandMark';
import LoadingText from '../common/LoadingText/LoadingText';
import UnreadBadge from '../common/UnreadBadge/UnreadBadge';
import api from '../../api/client';
import { ensureFreshAuth } from '../../utils/auth';
import { getAuthToken, getAuthUser } from '../../utils/authStorage';
import { inferRequiredRoleFromPath, setPostLoginRedirect } from '../../utils/postLoginRedirect';
import { preloadDotLottiePlayer } from '../../utils/dotLottiePlayerLoader';
import useCourseAlertSummary from '../../hooks/useCourseAlertSummary';
import useMessageUnreadSummary from '../../hooks/useMessageUnreadSummary';
import { useI18n } from '../../i18n/language';
import { createRouteIntentProps, preloadRoute } from '../../routePreloaders';
import useMediaQuery from '../../hooks/useMediaQuery';
import { getTimezoneRegionLabel } from '../../utils/timezoneRegion';
import { MobileHomeFilters, MobileSearchSheet } from './MobileHomeFilters';

const STUDENT_LISTINGS_SEARCH_EVENT = 'student:listings-search';
const loadTimezoneModal = () => import('../TimezoneModal/TimezoneModal');
const loadCourseTypeModal = () => import('../CourseTypeModal/CourseTypeModal');
const loadStudentAuthModal = () => import('../AuthModal/StudentAuthModal');
const loadCourseOnboardingModal = () => import('../CourseOnboardingModal/CourseOnboardingModal');
const LazyTimezoneModal = lazy(loadTimezoneModal);
const LazyCourseTypeModal = lazy(loadCourseTypeModal);
const LazyStudentAuthModal = lazy(loadStudentAuthModal);
const LazyCourseOnboardingModal = lazy(loadCourseOnboardingModal);

const preloadLazy = (loader) => {
  Promise.resolve()
    .then(loader)
    .catch(() => {});
};

function AnchoredLoadingFallback({
  anchorRef,
  width = 220,
  className = '',
  align = 'left',
  alignRef = null,
  alignOffset = 0,
  message = '加载中…',
}) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchorEl = anchorRef?.current;
      if (!anchorEl) return;

      const rect = anchorEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const minGap = 8;
      const baseLeft = align === 'right'
        ? rect.right - width + alignOffset
        : (alignRef?.current ? alignRef.current.getBoundingClientRect().left : rect.left);
      let left = baseLeft;
      const maxLeft = viewportWidth - width - minGap;

      if (left > maxLeft) left = Math.max(minGap, maxLeft);
      if (left < minGap) left = minGap;

      setPosition({ top: rect.bottom + 10, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [align, alignOffset, alignRef, anchorRef, width]);

  return (
    <div className="navbar-lazy-fallback" aria-live="polite">
      <div
        className={`navbar-lazy-panel ${className}`.trim()}
        role="status"
        style={{ position: 'fixed', top: position.top, left: position.left, width }}
      >
        <LoadingText text={message} />
      </div>
    </div>
  );
}

function CenteredLoadingFallback({ message = '加载中…' }) {
  return (
    <div className="navbar-lazy-centered-fallback" role="status" aria-live="polite">
      <div className="navbar-lazy-centered-card"><LoadingText text={message} /></div>
    </div>
  );
}

function StudentNavbar() {
  const { t } = useI18n();
  const timezoneRef = useRef(null);
  const courseTypeRef = useRef(null);
  const exactSearchInputRef = useRef(null);
  const searchBarRef = useRef(null);
  const userIconRef = useRef(null);
  const publishBtnRef = useRef(null);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [showCourseTypeModal, setShowCourseTypeModal] = useState(false);
  const [selectedCourseType, setSelectedCourseType] = useState('');
  // 精确搜索：输入 MentorID 或导师姓名
  const [exactSearch, setExactSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [isSearchBarActive, setIsSearchBarActive] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [forceLogin, setForceLogin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMentorRegistered, setIsMentorRegistered] = useState(false);
  const [showCourseRequestDraftModal, setShowCourseRequestDraftModal] = useState(false);
  const [checkingCourseRequestDrafts, setCheckingCourseRequestDrafts] = useState(false);
  const [showMobileExactSearch, setShowMobileExactSearch] = useState(false);
  // 精确搜索展开（覆盖左侧筛选）状态
  const [isExactExpanded, setIsExactExpanded] = useState(false);
  // 延后切换激活项，避免按下鼠标到弹窗出现之间的闪烁
  const [pendingFilter, setPendingFilter] = useState('');
  const selectedRegionLabel = getTimezoneRegionLabel(selectedRegion, t);
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = useMediaQuery('(max-width: 599px)');

  const isStudentActive = location.pathname.startsWith('/student');
  const isMentorActive = location.pathname.startsWith('/mentor');

  const [isExactAnimating, setIsExactAnimating] = useState(false);
  const { totalUnreadCount: messageUnreadCount } = useMessageUnreadSummary(isLoggedIn);
  const { newCourseCount } = useCourseAlertSummary({ enabled: isLoggedIn, view: 'student' });
  const totalBadgeCount = messageUnreadCount + newCourseCount;

  useEffect(() => {
    if (!isPhone) setShowMobileExactSearch(false);
  }, [isPhone]);

  // 初始化登录状态与监听登录变化
  useEffect(() => {
    const computeIsMentor = () => {
      const user = getAuthUser() || {};
      const role = user?.role || (Array.isArray(user?.roles) && user.roles.includes('mentor') ? 'mentor' : undefined);
      return role === 'mentor';
    };

    ensureFreshAuth(api);

    setIsLoggedIn(!!getAuthToken());

    setIsMentorRegistered(computeIsMentor());

    const onAuthChanged = (e) => {
      const next = !!(e?.detail?.isLoggedIn ?? getAuthToken());
      setIsLoggedIn(next);
      // 若登录状态或角色变化，刷新导师注册态
      if (e?.detail?.user || typeof e?.detail?.role !== 'undefined') {
        const role = e?.detail?.role ?? e?.detail?.user?.role;
        if (role) {
          setIsMentorRegistered(role === 'mentor');
        } else {
          setIsMentorRegistered(computeIsMentor());
        }
      } else {
        setIsMentorRegistered(computeIsMentor());
      }
    };
    window.addEventListener('auth:changed', onAuthChanged);

    const onStorage = (ev) => {
      if (ev.key === 'authToken') setIsLoggedIn(!!getAuthToken());
      if (ev.key === 'authUser' || ev.key === 'authToken') {
        setIsMentorRegistered(computeIsMentor());
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('auth:changed', onAuthChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // 监听全局登录需求事件，直接弹出登录框
  useEffect(() => {
    const onLoginRequired = (event) => {
      const detail = event?.detail || {};
      const from = typeof detail.from === 'string' ? detail.from.trim() : '';
      const requiredRole = typeof detail.requiredRole === 'string' && detail.requiredRole.trim()
        ? detail.requiredRole.trim()
        : inferRequiredRoleFromPath(from);

      if (from) {
        setPostLoginRedirect(from, requiredRole);
      } else {
        try {
          const { pathname, search, hash } = window.location;
          const current = `${pathname}${search || ''}${hash || ''}`;
          setPostLoginRedirect(current, inferRequiredRoleFromPath(current));
        } catch {}
      }
      setForceLogin(true);
      setShowAuthModal(true);
    };
    window.addEventListener('auth:login-required', onLoginRequired);
    return () => window.removeEventListener('auth:login-required', onLoginRequired);
  }, []);

  // 登录状态变更（注册成功自动登录 / 退出登录）时，在学生首页触发主页开场动画
  const firstAuthRef = useRef(true);
  useEffect(() => {
    if (firstAuthRef.current) { firstAuthRef.current = false; return; }
    if (!isLoggedIn && location.pathname.startsWith('/student')) {
      try { window.dispatchEvent(new Event('home:enter')); } catch {}
    }
  }, [isLoggedIn, location.pathname]);

  // 挂载后根据弹窗打开情况再切换激活项，避免点击瞬间的白->灰闪烁
  useEffect(() => {
    if (pendingFilter === 'timezone' && showTimezoneModal) {
      setActiveFilter('timezone');
      setIsSearchBarActive(true);
      setPendingFilter('');
    } else if (pendingFilter === 'courseType' && showCourseTypeModal) {
      setActiveFilter('courseType');
      setIsSearchBarActive(true);
      setPendingFilter('');
    }
  }, [pendingFilter, showTimezoneModal, showCourseTypeModal]);

  // 当精确搜索展开时，仅点击搜索框外部才收起
  useEffect(() => {
    if (!isExactExpanded) return;

    const handleOutside = (e) => {
      const bar = searchBarRef.current;
      if (bar && !bar.contains(e.target)) {
        setIsExactAnimating(true);      // ✅ 标记开始收起动画
        setIsExactExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
    };
  }, [isExactExpanded]);

  useEffect(() => {
    const bar = searchBarRef.current;
    if (!bar) return;
    
    const onEnd = (e) => {
      if (!isExactAnimating) return;
      // 这些属性任意一个过渡结束即可认为布局回位完成
      if (['flex-basis','width','opacity','padding','margin'].includes(e.propertyName)) {
        setIsExactAnimating(false);     // 动画结束
        setActiveFilter('');            // 现在再清理激活项
        // 如果你的逻辑是“没有精确输入就整体失焦”，可以按需清理：
        if (!exactSearch) setIsSearchBarActive(false);
      }
    };
  
    bar.addEventListener('transitionend', onEnd);
    return () => bar.removeEventListener('transitionend', onEnd);
  }, [isExactAnimating, exactSearch]);

  const applySearch = (overrides = {}) => {
    const nextRegion = typeof overrides.region === 'string' ? overrides.region : selectedRegion;
    const nextExactSearch = typeof overrides.exactSearch === 'string' ? overrides.exactSearch : exactSearch;
    const nextCourseType = typeof overrides.courseType === 'string' ? overrides.courseType : selectedCourseType;
    try {
      window.dispatchEvent(new CustomEvent(STUDENT_LISTINGS_SEARCH_EVENT, {
        detail: {
          region: nextRegion,
          exactSearch: nextExactSearch,
          courseType: nextCourseType,
        },
      }));
    } catch {}
  };

  const handlePublishCourseRequest = async () => {
    preloadRoute('studentCourseRequest')?.catch(() => {});
    preloadLazy(loadCourseOnboardingModal);
    preloadDotLottiePlayer();

    if (!isLoggedIn) {
      preloadLazy(loadStudentAuthModal);
      setPostLoginRedirect('/student/course-request', 'student');
      setForceLogin(true);
      setShowAuthModal(true);
      return;
    }

    if (checkingCourseRequestDrafts) return;

    setCheckingCourseRequestDrafts(true);
    try {
      ensureFreshAuth(api);
      const res = await api.get('/api/requests/drafts', { params: { limit: 50 } });
      const rows = Array.isArray(res?.data?.drafts) ? res.data.drafts : [];
      const hasExistingRequests = rows.length > 0;
      if (hasExistingRequests) {
        setShowCourseRequestDraftModal(true);
      } else {
        navigate('/student/course-request');
      }
    } catch {
      navigate('/student/course-request');
    } finally {
      setCheckingCourseRequestDrafts(false);
    }
  };

  const toggleAuthModal = () => {
    preloadLazy(loadStudentAuthModal);
    setForceLogin(false);
    setShowAuthModal((prev) => !prev);
  };

  const preloadStudentAuthMenu = () => {
    preloadLazy(loadStudentAuthModal);
  };

  const getMentorFeatureLabel = (value) => {
    const key = String(value || '').trim();
    if (!key) return '';
    const labels = {
      评分高: t('mentorFilter.highRating', '评分高'),
      经验丰富: t('mentorFilter.experienced', '经验丰富'),
      快速响应: t('mentorFilter.fastResponse', '快速响应'),
      双语授课: t('mentorFilter.bilingualTeaching', '双语授课'),
      QS前100: t('mentorFilter.qsTop100', 'QS前100'),
    };
    return labels[key] || key;
  };

  return (
    <header className="navbar navbar--student">
      {/* 顶部双层导航 */}
      <div className="navbar-top container">
        <div className="navbar-left">
          {/* 学生视图点击 LOGO 返回学生首页 */}
          <BrandMark className="nav-logo-text" to="/student" />
        </div>
        <div className="navbar-center">
          <nav className="nav-tabs">
            <button
              className={`nav-tab ${isStudentActive ? 'active' : ''}`}
              onClick={() => navigate('/student')}
            >
              {t('nav.students', '学生')}
            </button>
            <button
              className={`nav-tab ${isMentorActive ? 'active' : ''} ${!isMentorRegistered ? 'disabled' : ''}`}
              {...createRouteIntentProps('mentorHome')}
              onClick={() => { if (isMentorRegistered) navigate('/mentor'); }}
              disabled={!isMentorRegistered}
              aria-disabled={!isMentorRegistered}
            >
              {t('nav.mentors', '导师')}
            </button>
          </nav>
        </div>
        <div className="navbar-right">
          <button
            type="button"
            className="nav-link nav-text"
            ref={publishBtnRef}
            {...createRouteIntentProps('studentCourseRequest')}
            onClick={handlePublishCourseRequest}
            disabled={checkingCourseRequestDrafts}
          >
            {t('nav.publishCourseRequest', '发布课程需求')}
          </button>
          <span
            className="icon-circle nav-menu-trigger"
            ref={userIconRef}
            onMouseEnter={preloadStudentAuthMenu}
            onFocus={preloadStudentAuthMenu}
            onPointerDown={preloadStudentAuthMenu}
            onClick={toggleAuthModal}
          >
            {isLoggedIn ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <line x1="5" y1="8"  x2="20" y2="8"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <line x1="5" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <line x1="5" y1="16" x2="20" y2="16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            ) : (
              <i className="fa fa-user"></i>
            )}
            {isLoggedIn ? (
              <UnreadBadge
                count={totalBadgeCount}
                variant="nav"
                className="nav-unread-badge"
                ariaLabel={t('common.pendingReminders', '待处理提醒')}
              />
            ) : null}
          </span>
        </div>
      </div>

      <MobileHomeFilters
        ariaLabel={t('nav.homeFilters', '导师筛选')}
        showChevron={false}
        hideLabelWhenSelected
        filters={[
          {
            key: 'timezone',
            label: t('nav.timeZone', '时区'),
            value: selectedRegionLabel,
            buttonRef: timezoneRef,
            expanded: showTimezoneModal,
            onOpen: () => {
              preloadLazy(loadTimezoneModal);
              setShowTimezoneModal(true);
              setPendingFilter('timezone');
            },
            onClear: (event) => {
              event.stopPropagation();
              setSelectedRegion('');
              applySearch({ region: '' });
            },
          },
          {
            key: 'courseType',
            label: t('nav.mentorSpecialty', '导师特色'),
            value: getMentorFeatureLabel(selectedCourseType),
            buttonRef: courseTypeRef,
            expanded: showCourseTypeModal,
            onOpen: () => {
              preloadLazy(loadCourseTypeModal);
              setShowCourseTypeModal(true);
              setPendingFilter('courseType');
            },
            onClear: (event) => {
              event.stopPropagation();
              setSelectedCourseType('');
              applySearch({ courseType: '' });
            },
          },
          {
            key: 'exactSearch',
            label: t('nav.exactSearch', '精确搜索'),
            value: exactSearch,
            expanded: showMobileExactSearch,
            onOpen: () => setShowMobileExactSearch(true),
            onClear: (event) => {
              event.stopPropagation();
              setExactSearch('');
              applySearch({ exactSearch: '' });
            },
          },
        ]}
      />

      {/* 搜索栏 */}
      <div className="navbar-bottom container">
        <div
          ref={searchBarRef}
          className={`search-bar ${isSearchBarActive ? 'active' : ''} ${isExactExpanded ? 'exact-expanded' : ''} ${isExactAnimating ? 'is-animating' : ''}`}
        >
          <div className="search-filters">
            <div
              ref={timezoneRef}
              className={`search-item timezone ${activeFilter === 'timezone' ? 'active' : ''}`}
              onClick={() => {
                preloadLazy(loadTimezoneModal);
                setShowTimezoneModal(true);
                setPendingFilter('timezone');
              }}
            >
              <label>{t('nav.timeZone', '时区')}</label>
              <input
                type="text"
                placeholder={t('nav.chooseMentorTimeZone', '选择导师时区')}
                value={selectedRegionLabel}
                readOnly
              />
            </div>
            <div
              ref={courseTypeRef}
              className={`search-item course-type ${activeFilter === 'courseType' ? 'active' : ''}`}
              onClick={() => {
                preloadLazy(loadCourseTypeModal);
                setShowCourseTypeModal(true);
                setPendingFilter('courseType');
              }}
            >
              <label>{t('nav.mentorSpecialty', '导师特色')}</label>
              <input
                type="text"
                placeholder={t('nav.chooseMentorSpecialty', '选择导师特色')}
                value={getMentorFeatureLabel(selectedCourseType)}
                readOnly
              />
            </div>
            <div
              className={`search-item start-date ${activeFilter === 'startDate' ? 'active' : ''}`}
              onClick={() => {
                setActiveFilter('startDate');
                setIsSearchBarActive(true);
                setIsExactExpanded(true);
                try { exactSearchInputRef.current && exactSearchInputRef.current.focus(); } catch {}
              }}
            >
              <label>{t('nav.exactSearch', '精确搜索')}</label>
              <input
                ref={exactSearchInputRef}
                type="text"
                placeholder={t('nav.exactSearchPlaceholder', '输入导师的MentorID或姓名')}
                value={exactSearch}
                onChange={(e) => {
                  const v = e.target.value;
                  setExactSearch(v);
                  setIsExactExpanded(true);
                  setIsSearchBarActive(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applySearch();
                  }
                }}
                onFocus={() => {
                  setActiveFilter('startDate');
                  setIsSearchBarActive(true);
                  setIsExactExpanded(true);
                }}
                style={{ cursor: 'text' }}
              />
            </div>
          </div>
          <button type="button" className="search-btn" onClick={applySearch} aria-label={t('common.search', '搜索')}>
            <i className="fas fa-search"></i>
          </button>
        </div>
      </div>

      {/* 弹窗部分 */}
      {showTimezoneModal && (
        <Suspense
          fallback={(
            <AnchoredLoadingFallback
              anchorRef={timezoneRef}
              width={360}
              className="navbar-lazy-panel--wide"
              message={t('nav.loadingTimeZoneFilter', '正在加载时区筛选…')}
            />
          )}
        >
          <LazyTimezoneModal
            onClose={() => {
              setShowTimezoneModal(false);
              if (!pendingFilter) {
                setActiveFilter('');
                setIsSearchBarActive(false);
              }
            }}
            onSelect={(region) => {
              const next = region || '';
              setSelectedRegion(next);
              if (isPhone) applySearch({ region: next });
            }}
            anchorRef={timezoneRef}
            presentation={isPhone ? 'sheet' : 'anchored'}
          />
        </Suspense>
      )}

      {showCourseTypeModal && (
        <Suspense
          fallback={(
            <AnchoredLoadingFallback
              anchorRef={courseTypeRef}
              width={220}
              message={t('nav.loadingMentorSpecialty', '正在加载导师特色…')}
            />
          )}
        >
          <LazyCourseTypeModal
            onClose={() => {
              setShowCourseTypeModal(false);
              if (!pendingFilter) {
                setActiveFilter('');
                setIsSearchBarActive(false);
              }
            }}
            onSelect={(courseType) => {
              const next = courseType || '';
              setSelectedCourseType(next);
              if (isPhone) applySearch({ courseType: next });
            }}
            anchorRef={courseTypeRef}
            mode="studentFeatures"
            presentation={isPhone ? 'sheet' : 'anchored'}
          />
        </Suspense>
      )}

      {/* 首课日期弹窗已移除，改为直接输入 */}

      {showAuthModal && (
        <Suspense
          fallback={(
            <AnchoredLoadingFallback
              anchorRef={userIconRef}
              align="right"
              width={200}
              alignOffset={0}
              message={t('nav.loadingMenu', '正在加载菜单…')}
            />
          )}
        >
          <LazyStudentAuthModal
            onClose={() => { setShowAuthModal(false); setForceLogin(false); }}
            onPublishCourseRequest={handlePublishCourseRequest}
            anchorRef={userIconRef}
            leftAlignRef={publishBtnRef}
            forceLogin={forceLogin}
            isLoggedIn={isLoggedIn}
            unreadCount={messageUnreadCount}
            courseCount={newCourseCount}
          />
        </Suspense>
      )}

      {showCourseRequestDraftModal && (
        <Suspense fallback={<CenteredLoadingFallback message={t('nav.loadingCourseDrafts', '正在加载课程草稿…')} />}>
          <LazyCourseOnboardingModal
            showConfirmButton={false}
            includeSubmitted
            onCreateCourse={() => {
              preloadDotLottiePlayer();
              setShowCourseRequestDraftModal(false);
              navigate('/student/course-request');
            }}
            onClose={() => setShowCourseRequestDraftModal(false)}
          />
        </Suspense>
      )}

      <MobileSearchSheet
        open={showMobileExactSearch}
        title={t('nav.exactSearch', '精确搜索')}
        value={exactSearch}
        placeholder={t('nav.exactSearchPlaceholder', '输入导师的MentorID或姓名')}
        onChange={setExactSearch}
        onClose={() => setShowMobileExactSearch(false)}
        searchLabel={t('common.search', '搜索')}
        onSubmit={() => {
          applySearch({ exactSearch });
          setShowMobileExactSearch(false);
        }}
      />
    </header>
  );
}

export default StudentNavbar;
